/**
 * -----------------------------------------------------------------------------
 * Popup - Create New User
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new user in the organization.
 */

import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateOrganizationRoleMutation, useCreateOrganizationUserMutation, useOrganizationRolesPermissionsQuery } from "../../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../../api/network/errorCodes";
import { TModalsState, setModalsData, TListingQueryParams } from "../../../../api/store/commonSlice";
import { TLegacyUserRole } from "../../../../api/types/UserRole";
import { AdminFormFieldDropdown, AdminFormFieldInput, AdminFormFieldSubmit } from "../../../../components/admin/formFields";
import { routeUrls } from "../../../../navigation/routeUrls";
import { userRoleDisplayName } from "../../../../utils/user";
import { formInitialValues, TAddFormFieldNames, YupValidationSchema } from "../validation";
import { dbTimeToJsTime, formattedDateTime, jsTimeToDBTime } from "../../../../utils/dateTime";
import { useAddMaintenanceDetailsMutation } from "../../../../api/network/maintenanceApiService";

const AddMaintenanceDetails = () => {
  const { deviceId } = useParams<{ deviceId: string }>();

  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'admins.maintenance.detailsPage.add_maintenance_form' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const listingQueryParams: TListingQueryParams = useSelector((state: any) => state.commonReducer.listingQueryParams);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // user mutation
  const [addMaintenanceDetailsAPITrigger] = useAddMaintenanceDetailsMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showAddMaintenanceDetails: false }));
  };

  if (modalsState.showAddMaintenanceDetails === false) return null;

  return (
    <>
      <div className="justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-overlay outline-none focus:outline-none">
        <div className="fixed w-full h-screen bg-modal-overlay z-overlay" onClick={hideModal}></div>
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[560px] z-modal">
          {/* <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none"> */}
          <Formik
            initialValues={formInitialValues}
            validationSchema={YupValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              // if(APP_CONFIG.TOASTS.INFO) toast.info(t('loginScreen.toast.logging_you_in'));
              addMaintenanceDetailsAPITrigger({
                deviceid: deviceId,
                servicetimestamp: jsTimeToDBTime(values?.service_date),
                serviceprovider: values?.provider,
                providercontact: values?.contact_name,
                provideremail: values?.contact_email,
                providerphone: values?.contact_phone,
                servicenotes: values?.notes,
                servicecost: values?.cost,
                maintname: modalsState?.maintName,
                maintbucket: modalsState?.maintBucket
              })
                .unwrap()
                .then((data) => {
                  if (!!data?.deviceid) {
                    dispatch(setModalsData({ ...modalsState, showAddMaintenanceDetails: false, maintName: "", maintBucket: "" }));
                    toast.success(t('toast.add_success'), { autoClose: 10000 });
                    // navigate(`${routeUrls.dashboardChildren.adminChildren.roles}/${data?.roleid}`, { state: { new: true } });
                  } else {
                    toast.error(t('toast.add_failed'));
                  }
                })
                .catch((error) => {
                  const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.add_failed')];
                  toast.error(errors?.join(' '));
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
          >{({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            dirty,
            setFieldValue,
            setFieldTouched
          }) => {
            const invalidFields =
              Object.keys(errors)
                .filter(key => !!errors[key as TAddFormFieldNames]);
            const isFormValid = invalidFields.length === 0 && dirty;

            return (
              <form className="p-8 bg-white grid grid-cols-6 gap-6 rounded-3xl shadow-2xl" onSubmit={handleSubmit}>

                <h2 className="col-span-6 text-3xl font-bold text-heading-black">
                  {t("heading")}
                </h2>

                <p className="col-span-6 mt-0 font-bold leading-relaxed text-gray-500">
                  {t("sub_heading")}
                </p>

                <AdminFormFieldInput
                  label={t("service_date")}
                  type="date-picker"
                  id="service_date"
                  name="service_date"
                  value={formattedDateTime(values.service_date, 'yyyy-MM-dd')}
                  onDatepickerChange={(_,value) => {
                    setFieldValue("service_date", value);
                  }}
                  onBlur={(e) => {
                    setFieldTouched("service_date", true);
                    handleBlur(e);
                  }}
                  error={errors.service_date}
                  touched={touched.service_date}
                  detailsFormField={true}
                  size="lg"
                />

                <AdminFormFieldInput
                  label={t('provider')}
                  type="text"
                  id="provider"
                  name="provider"
                  placeholder={t('provider_placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.provider}
                  touched={touched.provider}
                  error={errors.provider} />

                <AdminFormFieldInput
                  label={t('contact_name')}
                  type="text"
                  id="contact_name"
                  name="contact_name"
                  placeholder={t('contact_name_placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.contact_name}
                  touched={touched.contact_name}
                  error={errors.contact_name} />

                <AdminFormFieldInput
                  label={t('contact_phone')}
                  type="text"
                  id="contact_phone"
                  name="contact_phone"
                  placeholder={t('contact_phone_placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.contact_phone}
                  touched={touched.contact_phone}
                  error={errors.contact_phone} />

                <AdminFormFieldInput
                  label={t('contact_email')}
                  type="text"
                  id="contact_email"
                  name="contact_email"
                  placeholder={t('contact_email_placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.contact_email}
                  touched={touched.contact_email}
                  error={errors.contact_email} />

                <AdminFormFieldInput
                  label={t('notes')}
                  type="text"
                  id="notes"
                  name="notes"
                  placeholder={t('notes_placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.notes}
                  touched={touched.notes}
                  error={errors.notes} />

                <AdminFormFieldInput
                  label={t('cost')}
                  type="text"
                  id="cost"
                  name="cost"
                  placeholder={t('cost_placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.cost}
                  touched={touched.cost}
                  error={errors.cost}
                  bottomLabel="in Dollars"
                />

                <AdminFormFieldSubmit
                  label={t('submit_button')}
                  variant="primary"
                  disabled={!isFormValid || isSubmitting /* || isLoading*/}
                />

                <AdminFormFieldSubmit
                  label={tMain('cancel')}
                  type="button"
                  variant="danger-transparent"
                  disabled={isSubmitting /* || isLoading*/}
                  onClick={hideModal}
                />
              </form>
            )
          }}
          </Formik>
        </div>
      </div>
    </>
  )
};
export default AddMaintenanceDetails;